export const ORG_SETTINGS_PAGES = [
  'general',
  'api-keys',
  'features',
  'integrations',
  'team',
  'usage',
  'billing',
] as const

export type OrgSettingsPage = (typeof ORG_SETTINGS_PAGES)[number]

/** @deprecated Tab ids from the old `/organization/settings` hub. */
export const ORG_SETTINGS_TABS = ['general', 'api-keys', 'features', 'integrations'] as const
export type OrgSettingsTab = (typeof ORG_SETTINGS_TABS)[number]

export const ORG_SETTINGS_PAGE_SEGMENTS: Record<OrgSettingsPage, string> = {
  general: 'general',
  'api-keys': 'api-keys',
  features: 'features',
  integrations: 'integrations',
  team: 'team',
  usage: 'usage',
  billing: 'billing',
}

export const ORG_SETTINGS_PAGE_ROUTE_SEGMENTS = ORG_SETTINGS_PAGES.map(
  (page) => ORG_SETTINGS_PAGE_SEGMENTS[page],
)

/** Pages rendered by OrganizationSettings (not Team / Usage / Billing). */
export const ORG_SETTINGS_HUB_PAGE_ROUTE_SEGMENTS = ['general', 'api-keys', 'features', 'integrations'] as const

/** @deprecated */
export const ORG_SETTINGS_TAB_ROUTE_SEGMENTS = ['api-keys', 'features', 'integrations'] as const

export const ORG_SETTINGS_PAGE_TITLES: Record<OrgSettingsPage, string> = {
  general: 'General',
  'api-keys': 'API keys',
  features: 'Features',
  integrations: 'Integrations',
  team: 'Team',
  usage: 'Usage',
  billing: 'Billing',
}

export function organizationPagePath(page: OrgSettingsPage): string {
  return `/organization/${ORG_SETTINGS_PAGE_SEGMENTS[page]}`
}

export const ORG_INTEGRATION_SUBPAGES = ['slack', 'webhooks', 'github', 'origin', 'gitea', 'gitlab', 'bitbucket'] as const
export type OrgIntegrationSubPage = (typeof ORG_INTEGRATION_SUBPAGES)[number]

/** GitLab and Bitbucket stay in the type map but are not shown until we ship them. */
const ORG_INTEGRATION_SUBPAGES_HIDDEN: ReadonlySet<OrgIntegrationSubPage> = new Set(['gitlab', 'bitbucket'])

export function isOrganizationIntegrationNavVisible(subPage: OrgIntegrationSubPage): boolean {
  return !ORG_INTEGRATION_SUBPAGES_HIDDEN.has(subPage)
}

export const ORG_INTEGRATION_SUBPAGE_TITLES: Record<OrgIntegrationSubPage, string> = {
  slack: 'Slack',
  webhooks: 'Webhooks',
  github: 'GitHub',
  origin: 'Origin',
  gitea: 'Gitea',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
}

/** Page heading; Origin uses the product name on its settings screen. */
export const ORG_INTEGRATION_PAGE_TITLES: Record<OrgIntegrationSubPage, string> = {
  slack: 'Slack',
  webhooks: 'Webhooks',
  github: 'GitHub',
  origin: 'Cursor Origin',
  gitea: 'Gitea',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
}

export function organizationIntegrationsPath(sub?: OrgIntegrationSubPage): string {
  return sub ? `/organization/integrations/${sub}` : '/organization/integrations'
}

export function parseOrganizationIntegrationsSubPage(
  pathname: string,
): OrgIntegrationSubPage | null {
  const prefix = '/organization/integrations'
  const normalized = pathname.replace(/\/$/, '')
  if (normalized === prefix) return null
  if (!normalized.startsWith(`${prefix}/`)) return null
  const sub = normalized.slice(prefix.length + 1)
  if (ORG_INTEGRATION_SUBPAGES.includes(sub as OrgIntegrationSubPage)) {
    return sub as OrgIntegrationSubPage
  }
  return null
}

export function isOrganizationIntegrationsPath(pathname: string): boolean {
  return parseOrganizationIntegrationsSubPage(pathname) != null ||
    pathname.replace(/\/$/, '') === '/organization/integrations'
}

/** @deprecated Use organizationPagePath. */
export function organizationSettingsTabPath(tab: OrgSettingsTab): string {
  return organizationPagePath(tab)
}

export function organizationSettingsPathSegment(pathname: string): string | null {
  const prefix = '/organization/'
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest) return null
  if (rest === 'integrations' || rest.startsWith('integrations/')) {
    return 'integrations'
  }
  if (rest.includes('/')) return null
  if (!ORG_SETTINGS_HUB_PAGE_ROUTE_SEGMENTS.includes(rest as (typeof ORG_SETTINGS_HUB_PAGE_ROUTE_SEGMENTS)[number])) {
    return null
  }
  return rest
}

export function parseOrganizationSettingsPageSegment(
  segment: string | null | undefined,
): OrgSettingsTab {
  if (!segment) return 'general'
  return normalizeOrgSettingsTab(segment)
}

/** @deprecated */
export function normalizeOrgSettingsTab(raw: string | null): OrgSettingsTab {
  const t = (raw ?? 'general').trim().toLowerCase()
  if (t === 'api_keys' || t === 'apikeys') return 'api-keys'
  if (ORG_SETTINGS_TABS.includes(t as OrgSettingsTab)) return t as OrgSettingsTab
  return 'general'
}

/** @deprecated */
export function parseOrganizationSettingsPathSegment(segment: string | null | undefined): OrgSettingsTab {
  if (!segment) return 'general'
  return normalizeOrgSettingsTab(segment)
}

export function legacyOrgSettingsTabToPage(tab: OrgSettingsTab): OrgSettingsPage {
  return tab
}

export function legacyOrgSettingsSegmentToPage(segment: string | null | undefined): OrgSettingsPage {
  if (!segment) return 'general'
  if (segment === ORG_SETTINGS_PAGE_SEGMENTS.general) return 'general'
  if (segment === ORG_SETTINGS_PAGE_SEGMENTS['api-keys']) return 'api-keys'
  if (segment === ORG_SETTINGS_PAGE_SEGMENTS.features) return 'features'
  if (segment === ORG_SETTINGS_PAGE_SEGMENTS.integrations) return 'integrations'
  return legacyOrgSettingsTabToPage(normalizeOrgSettingsTab(segment))
}

/** @deprecated */
export function organizationSettingsPathNeedsRewrite(pathname: string, tab: OrgSettingsTab): string | null {
  const want = organizationPagePath(tab)
  const current = pathname.replace(/\/$/, '')
  const wantPath = want.replace(/\/$/, '')
  return current === wantPath ? null : want
}

export function legacyOrganizationSettingsHubSegment(pathname: string): string | null {
  const prefix = '/organization/settings/'
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  return rest
}

export function isLegacyOrganizationSettingsHubPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '')
  return normalized === '/organization/settings' || pathname.startsWith('/organization/settings/')
}

export type OrganizationNavItem = {
  page: OrgSettingsPage
  label: string
  path: string
}

export type OrganizationIntegrationNavItem = {
  subPage: OrgIntegrationSubPage
  label: string
  path: string
  beta?: boolean
}

export function getOrganizationNavItems(): OrganizationNavItem[] {
  return ORG_SETTINGS_PAGES.map((page) => ({
    page,
    label: ORG_SETTINGS_PAGE_TITLES[page],
    path:
      page === 'integrations'
        ? organizationIntegrationsPath()
        : organizationPagePath(page),
  }))
}

export function getOrganizationIntegrationNavItems(): OrganizationIntegrationNavItem[] {
  return ORG_INTEGRATION_SUBPAGES.filter(isOrganizationIntegrationNavVisible).map((subPage) => ({
    subPage,
    label: ORG_INTEGRATION_SUBPAGE_TITLES[subPage],
    path: organizationIntegrationsPath(subPage),
    beta: subPage === 'origin',
  }))
}

export function isOrganizationNavItemActive(pathname: string, path: string): boolean {
  const current = pathname.replace(/\/$/, '')
  const target = path.replace(/\/$/, '')
  if (target.startsWith('/organization/integrations')) {
    return isOrganizationIntegrationsPath(current)
  }
  return current === target
}

export function isOrganizationPath(pathname: string): boolean {
  if (isLegacyOrganizationSettingsHubPath(pathname)) return true
  const prefix = '/organization/'
  if (!pathname.startsWith(prefix)) return false
  const segment = pathname.slice(prefix.length).replace(/\/$/, '').split('/')[0]
  return ORG_SETTINGS_PAGE_ROUTE_SEGMENTS.includes(segment)
}
